import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "Gross margin is revenue minus the direct cost of producing goods, divided by revenue. Operating margin goes further: it also subtracts overheads like R&D, marketing, and admin. Gross margin shows how profitable a product is. Operating margin shows how profitable the whole business is. A wide gap between them means heavy operating overhead.";

const FAQS = [
  { q: 'Which margin matters more?', a: 'Operating margin is more comprehensive because it includes overheads. Gross margin is more useful for spotting pricing power. Most investors look at both side by side.' },
  { q: 'What is a "good" gross margin?', a: 'It depends on the industry. Software typically has 70–90% gross margin. Retailers and grocers can run at 20–30%. Compare a company only to its direct sector peers.' },
  { q: 'Can margins be negative?', a: 'Yes. Early-stage companies often have negative operating margins because overhead is large relative to revenue. As they scale, margins usually improve.' },
  { q: 'Is operating margin the same as profit margin?', a: 'No. Profit margin (also called net margin) sits below operating margin and also subtracts interest and taxes. Net margin is the most "complete" measure.' },
  { q: 'How can a company with high gross margin still lose money?', a: 'If overhead (sales, marketing, R&D) eats up the entire gross profit, operating margin can be negative. Many SaaS startups are exactly this.' },
];

export default function GrossVsOperatingMarginPage() {
  return (
    <BlogArticleLayout
      slug="gross-vs-operating-margin"
      title="Gross Margin vs Operating Margin: What's the Difference?"
      description="Two margins, two very different stories. Learn what each one tells you about a business and how to compare them across sectors."
      keywords="gross margin vs operating margin, gross margin, operating margin, profit margin, gross profit, operating profit, margin formula, how to calculate margin"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['revenue-vs-earnings', 'what-is-free-cash-flow', 'stock-picking']}
    >
      <section>
        <h2>What is gross margin?</h2>
        <p>
          Gross margin is the percentage of revenue left after subtracting the direct cost of producing the product or service.
          The formula is: <strong>Gross Margin = (Revenue − Cost of Goods Sold) ÷ Revenue</strong>.
        </p>
        <p>
          For Apple, COGS is the cost of materials and assembly for each iPhone. For Microsoft Azure, COGS is data-centre hosting costs. Anything not tied directly to making the product belongs in operating expenses, not COGS.
        </p>
      </section>

      <section>
        <h2>What is operating margin?</h2>
        <p>
          Operating margin subtracts <em>both</em> COGS and operating expenses (R&D, sales, marketing, general & admin) from revenue.
          Formula: <strong>Operating Margin = Operating Income ÷ Revenue</strong>.
        </p>
        <p>
          It is the closest thing to "how profitable is the actual business" before interest and taxes get involved. A company can have a great gross margin and still lose money if it overspends on overhead.
        </p>
      </section>

      <section>
        <h2>How do the two margins compare across sectors?</h2>
        <p>
          Sector matters enormously:
        </p>
        <ul>
          <li><strong>Software / SaaS</strong> — gross margin 70–90%, operating margin 15–35%. Big gap goes to R&D and sales.</li>
          <li><strong>Branded consumer (e.g. Coca-Cola)</strong> — gross margin 50–60%, operating margin 25–30%. Marketing eats most of the gap.</li>
          <li><strong>Retailers (e.g. Walmart)</strong> — gross margin 20–25%, operating margin 4–6%. Thin everywhere.</li>
          <li><strong>Banks</strong> — margins are usually expressed as net interest margin instead.</li>
        </ul>
        <p>
          A grocer with 25% gross margin is doing fine. A software company with 25% gross margin is in trouble.
        </p>
      </section>

      <section>
        <h2>Why does the gap between gross and operating margin matter?</h2>
        <p>
          A wide gap means the company spends a lot to convert raw production capability into sales and growth.
          That can be fine if growth is genuinely materialising — Salesforce ran a small operating margin for years while compounding revenue 25%+ a year.
          It is a warning sign if growth has stalled and the spend is only there to keep the wheels turning.
        </p>
        <p>
          Pair this with our guide on <Link to="/blog/what-is-free-cash-flow">free cash flow</Link> to see whether the spending actually generates cash.
        </p>
      </section>

      <section>
        <h2>How do margins help when picking stocks?</h2>
        <p>
          Three quick checks once you have both margins:
        </p>
        <ul>
          <li><strong>Trend.</strong> Is operating margin expanding or contracting year over year? Expansion is one of the most powerful drivers of long-term returns.</li>
          <li><strong>Versus peers.</strong> A company with structurally higher margins than its competitors usually has pricing power, scale, or a better business model.</li>
          <li><strong>Resilience.</strong> Stable margins through recessions point to a defensible product. Volatile margins point to a commodity-like business.</li>
        </ul>
        <p>
          See our <Link to="/blog/stock-picking">stock picking guide</Link> for how margins fit into a broader analysis.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
